import { boolean } from 'yup';
import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	AchievementFilter,
	TaskAchievementCumulativeSeriesDTO,
	TaskAchievementRewardDetailDTO,
	TaskAchievementRewardDetailSingleRequest,
} from '../DTO/task/achievement.interface';

export const achievementApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getAchievementList: builder.query<
			SuccessResponse<Paginated<TaskAchievementCumulativeSeriesDTO>>,
			AchievementFilter
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['AchievementList'],
		}),
		getAchievementSortList: builder.query<
			SuccessResponse<TaskAchievementCumulativeSeriesDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/task/achievement/cumulative/series/sort`,
				method: 'GET',
			}),
		}),
		sortAchievementList: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AchievementList'],
		}),
		getAchievementDetail: builder.query<
			SuccessResponse<TaskAchievementCumulativeSeriesDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/task/achievement/cumulative/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['AchievementDetail'],
		}),
		createAchievement: builder.mutation<
			SuccessResponse<any>,
			TaskAchievementCumulativeSeriesDTO
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/series`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['AchievementList'],
		}),
		updateAchievement: builder.mutation<
			SuccessResponse<any>,
			TaskAchievementCumulativeSeriesDTO
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AchievementDetail'],
		}),
		getAchievementSingleList: builder.query<
			SuccessResponse<Paginated<TaskAchievementRewardDetailDTO>>,
			AchievementFilter
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/series/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['AchievementSingleList'],
		}),
		createAchievementSingle: builder.mutation<
			SuccessResponse<any>,
			TaskAchievementRewardDetailSingleRequest
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/series/${body.seriesId}`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['AchievementSingleList'],
		}),
		updateAchievementSingle: builder.mutation<
			SuccessResponse<any>,
			TaskAchievementRewardDetailSingleRequest
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/single/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AchievementSingleDetail'],
		}),
		getAchievementSingleDetail: builder.query<
			SuccessResponse<TaskAchievementRewardDetailSingleRequest>,
			string
		>({
			query: (id) => ({
				url: `/web/task/achievement/cumulative/single/${id}`,
				method: 'GET',
			}),
			providesTags: ['AchievementSingleDetail'],
		}),
		getAchievementSingleSortList: builder.query<
			SuccessResponse<TaskAchievementRewardDetailSingleRequest[]>,
			string
		>({
			query: (id) => ({
				url: `/web/task/achievement/cumulative/series/${id}/sort`,
				method: 'GET',
			}),
		}),
		setAchievementSingleStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: { published: boolean; id: string }[] }
		>({
			query: (body) => ({
				url: `/web/task/achievement/cumulative/single/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AchievementSingleList', 'AchievementSingleDetail'],
		}),
		deleteAchievementSingle: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/task/achievement/cumulative/single/${id}`,
				method: 'DELETE',
			}),
		}),
		deleteAchievement: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/task/achievement/cumulative/series/${id}`,
				method: 'DELETE',
			}),
		}),
	}),
});

export const {
	useGetAchievementListQuery,
	useGetAchievementSortListQuery,
	useSortAchievementListMutation,
	useGetAchievementDetailQuery,
	useCreateAchievementMutation,
	useUpdateAchievementMutation,
	useGetAchievementSingleListQuery,
	useCreateAchievementSingleMutation,
	useUpdateAchievementSingleMutation,
	useGetAchievementSingleDetailQuery,
	useGetAchievementSingleSortListQuery,
	useSetAchievementSingleStatusMutation,
	useDeleteAchievementSingleMutation,
	useDeleteAchievementMutation,
} = achievementApiSlice;
